import type { IToastProps } from 'components/Toast';
import { State } from 'utils/state';
import { shouldShowAlert, getAlertConfig, interceptAlert } from 'utils/alerts';

export interface IToastNotification {
  toasts: IToastProps[];
  isBeingRendered?: boolean;
}

const defaults: Partial<IToastProps> = {
  linkText: '',
  message: '',
  title: '',
  newTab: false,
  timeout: 5000,
  to: '',
};

export const toastNotification = new State<IToastNotification>({ toasts: [], isBeingRendered: false });

export function showToast(toastProps: IToastProps) {
  // Ensure notificationId is defined, generate unique ID if missing
  const notificationId = toastProps.notificationId || `toast_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  // If this notification ID is already in the stack, don't add it again
  const currentState = toastNotification.get();
  if (currentState.toasts.some((toast) => toast.notificationId === notificationId)) {
    return;
  }

  // Check alert filter configuration
  const suppressed = !shouldShowAlert(notificationId, getAlertConfig()?.toasts);

  // Notify interceptor (runtime re-emits as event)
  const proceed = interceptAlert({
    kind: 'toast',
    suppressed,
    notificationId,
    type: toastProps.type,
    title: toastProps.title,
    message: toastProps.message,
    linkText: toastProps.linkText,
    to: toastProps.to,
    timeout: toastProps.timeout,
  });

  if (!proceed || suppressed) {
    return;
  }

  const newToast: IToastProps = { ...defaults, ...toastProps, notificationId };

  // Add to the stack
  toastNotification.setValues({ toasts: [...currentState.toasts, newToast] });
}

export function removeToast(notificationId: string) {
  const currentState = toastNotification.get();
  const updatedToasts = currentState.toasts.filter((toast) => toast.notificationId !== notificationId);
  toastNotification.setValues({ toasts: updatedToasts });
}

export function resetToastState() {
  toastNotification.setValues({ toasts: [], isBeingRendered: false }, { reset: true });
}

export function claimToastRendering(): boolean {
  const currentState = toastNotification.get();
  if (currentState.toasts.length > 0 && !currentState.isBeingRendered) {
    toastNotification.setValues({ isBeingRendered: true });
    return true;
  }
  return false;
}

// Reset the rendering flag when all toasts are removed
toastNotification.on('change', (changedKeys, _changedValues, _oldValues, _values, currentState) => {
  if (changedKeys.includes('toasts') && currentState.toasts.length === 0) {
    toastNotification.setValues({ isBeingRendered: false });
  }
});

export default toastNotification;
